import { ReactNode, useEffect, useState } from "react";
import { getMe, updateUser } from "../../features/search/service/user-service";
import { getRoleById } from "../../features/search/service/role-service";
import { User } from "../../shared/types/user";
import { Role } from "../../shared/types/role";
import { MenuTop } from '../../components/menu/top/menu-top';
import { useTranslation } from "../../shared/i18n/i18n-context";
import { Button, TextField } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import { PageBreadcrumb } from "../../components/page-breadcrumb/page-breadcrumb";
import { ProjectLoader } from "../../components/project-loader/project-loader";
import './styles/profile.css';

type ProfileInfoItem = {
    label: string;
    value: string;
    icon: ReactNode;
};

export const Profile = () => {
    const { t } = useTranslation('profile');
    const { t: menuT } = useTranslation('menu');
    const { t: commonT } = useTranslation('common');
    const [me, setMe] = useState<User | null>(null);
    const [meRole, setMeRole] = useState<Role | null>(null);
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoadingData(true);
                const userData = await getMe();
                setMe(userData);

                // Buscar a role pelo role_id
                if (userData.role_id) {
                    const roleData = await getRoleById(userData.role_id);
                    setMeRole(roleData);
                }
            } catch (error) {
                console.error('❌ Erro ao buscar dados do usuário:', error);
            } finally {
                setLoadingData(false);
            }
        };
        fetchUser();
    }, []);

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            return;
        }

        if (newPassword !== confirmPassword) {
            return;
        }

        if (newPassword.length < 6) {
            return;
        }

        if (currentPassword === newPassword) {
            return;
        }

        setLoading(true);

        try {
            const userData: any = {
                name: me?.name!,
                email: me?.email!,
                password: newPassword,
                roleId: me?.role_id,
                companyId: me?.companyId
            };

            const response = await updateUser(me?.id!, userData);
            
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            if (response) {
                setMe(response);
            }
        } catch (error) {
            console.error('❌ Erro ao alterar senha:', error);
        } finally {
            setLoading(false);
        }
    };

    const profileInfo: ProfileInfoItem[] = [
        {
            label: t('name'),
            value: me?.name || commonT('loading'),
            icon: <PersonIcon />
        },
        {
            label: t('email'),
            value: me?.email || commonT('loading'),
            icon: <MailOutlineIcon />
        },
        {
            label: t('company'),
            value: me?.company?.name || commonT('notAvailable'),
            icon: <BusinessIcon />
        },
        {
            label: t('role'),
            value: meRole?.name || commonT('notAvailable'),
            icon: <BadgeIcon />
        }
    ];

    if (loadingData) {
        return (
            <div>
                <MenuTop />
                <main className="profile-container">
                    <ProjectLoader label={commonT('loading')} />
                </main>
            </div>
        );
    }

    return (
        <div>
            <MenuTop />
            <div className="profile-container">
                <header className="profile-header">
                    <PageBreadcrumb
                        items={[
                            { label: menuT('home'), to: '/home' },
                            { label: t('title') }
                        ]}
                    />
                    <h1>{t('title')}</h1>
                </header>

                <section className="profile-card profile-card-active">
                    <div className="profile-card-marker">
                        <ContactPageOutlinedIcon />
                    </div>
                    <div className="profile-card-content">
                        <div className="profile-card-title-row">
                            <span className="profile-card-label">{t('userInformation')}</span>
                            <strong>{me?.name || commonT('loading')}</strong>
                        </div>

                        <div className="profile-info-grid">
                            {profileInfo.map((item) => (
                                <div className="profile-info-item" key={item.label}>
                                    <span className="profile-info-icon" aria-hidden="true">
                                        {item.icon}
                                    </span>
                                    <span className="profile-info-text">
                                        <span className="profile-info-label">{item.label}</span>
                                        <strong>{item.value}</strong>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="profile-card profile-password-card">
                    <div className="profile-card-marker profile-card-marker-secondary">
                        <LockOutlinedIcon />
                    </div>
                    <div className="profile-card-content">
                        <div className="profile-card-title-row">
                            <span className="profile-card-label">{t('changePassword')}</span>
                            <strong>{t('changePassword')}</strong>
                        </div>

                        <div className="profile-password-grid">
                            <TextField
                                id="id-input-current-password"
                                label={t('currentPassword')}
                                type="password"
                                value={currentPassword}
                                onChange={(event) => setCurrentPassword(event.target.value)}
                                autoComplete="current-password"
                                fullWidth
                            />

                            <TextField
                                id="id-input-new-password"
                                label={t('newPassword')}
                                type="password"
                                value={newPassword}
                                onChange={(event) => setNewPassword(event.target.value)}
                                autoComplete="new-password"
                                fullWidth
                            />

                            <TextField
                                id="id-input-confirm-password"
                                label={t('confirmNewPassword')}
                                type="password"
                                value={confirmPassword}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                                autoComplete="new-password"
                                fullWidth
                            />
                        </div>

                        <div className="password-requirements">
                            <p>{t('passwordRequirement')}</p>
                        </div>

                        <div className="profile-button-row">
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleChangePassword}
                                disabled={loading}
                            >
                                {loading ? t('changing') : t('changePassword')}
                            </Button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
